// The prompt book page layout ("overlay", page layout ticket 0011): the source page is drawn as it
// is, marks go over the script, and each moment, standby and note gets one label block in the outer
// margin (chosen by the printed page number), joined to its mark by a leader. Blocks are packed
// greedily down the margin in show order; a block that would sit too far from its line becomes a
// callout beside the mark instead.
//
// Pure: data in, drawing primitives out, in display space (points, origin top-left, y down, after
// /Rotate). Text is measured from the bundled font's width table, so the SVG preview and the PDF
// export make identical decisions.

import { type DocIndex, standbyCoverage, standbyText } from "../format/derive.ts";
import { displaySize, rectToDisplay, toDisplay } from "../format/geometry.ts";
import type { Anchor, Cue, Moment, Note, Page, Rect, Standby } from "../format/types.ts";
import { inkOn, readableOnWhite } from "./color.ts";
import metrics from "./glyph-widths.json" with { type: "json" };

export type FontStyle = "regular" | "bold" | "italic";

export type Prim =
  | { t: "rect"; x: number; y: number; w: number; h: number; fill?: string; fillOpacity?: number; stroke?: string; strokeWidth?: number; strokeOpacity?: number; dash?: number[] }
  | { t: "line"; x1: number; y1: number; x2: number; y2: number; stroke: string; width: number; opacity?: number; dash?: number[] }
  | { t: "circle"; cx: number; cy: number; r: number; fill?: string; stroke?: string; strokeWidth?: number }
  | { t: "poly"; points: [number, number][]; fill: string; opacity?: number }
  | { t: "text"; x: number; y: number; text: string; size: number; font: FontStyle; fill: string; clip?: Rect };

export interface LayoutOptions {
  /** Department ids to include; null means all. */
  departments: ReadonlySet<string> | null;
  /** Note layer ids to include; null means all. */
  layers: ReadonlySet<string> | null;
  /** Width of the label column in the outer margin, in points. */
  margin?: number;
  /** A short line printed at the foot of the label column (which book, date, commit). */
  stamp?: string | null;
}

export interface PageLayout {
  page: number;
  /** Display size in points. */
  width: number;
  height: number;
  side: "left" | "right";
  prims: Prim[];
  /** Blocks that didn't fit beside their line and were drawn over the script. */
  callouts: number;
}

export const STYLE = {
  // Sizes from Brendan's review of v1 (2026-10-26): the spike's 6 pt labels were too small to read.
  margin: 110,
  edge: 3,
  labelSize: 9,
  labelLine: 10.8,
  noteSize: 8,
  noteLine: 9.6,
  blockGap: 3,
  calloutDrift: 40,
  calloutWidth: 150,
  stampSize: 6,
  ink: "#212121",
  grey: "#666666",
  white: "#ffffff",
} as const;

type Widths = { missing: number; widths: Record<string, number> };
const FONTS = metrics as unknown as { unitsPerEm: number } & Record<FontStyle, Widths>;

export function textWidth(text: string, size: number, font: FontStyle): number {
  const f = FONTS[font];
  let units = 0;
  for (const ch of text) units += f.widths[ch.codePointAt(0) as number] ?? f.missing;
  return (units * size) / FONTS.unitsPerEm;
}

/** Greedy word wrap; words wider than the line are broken. Always at least one line. */
export function wrap(text: string, size: number, font: FontStyle, width: number): string[] {
  const out: string[] = [];
  for (const para of text.split(/\r?\n/)) {
    let line = "";
    for (const word of para.split(/\s+/).filter(Boolean)) {
      const next = line ? `${line} ${word}` : word;
      if (textWidth(next, size, font) <= width) {
        line = next;
        continue;
      }
      if (line) out.push(line);
      let w = word;
      while (textWidth(w, size, font) > width && w.length > 1) {
        let k = w.length - 1;
        while (k > 1 && textWidth(w.slice(0, k), size, font) > width) k--;
        out.push(w.slice(0, k));
        w = w.slice(k);
      }
      line = w;
    }
    out.push(line);
  }
  return out.length ? out : [""];
}

const INSIDE = {
  size: 12,
  padding: 2,
  line: 16.4,
  // Bundled Noto Sans Regular: ascender 1069, descender -293, unitsPerEm 1000.
  // Padding also contains its supported glyphs' left/right ink overhangs (< 1 pt).
  ascent: 12 * 1.069,
} as const;

export interface InsideNoteLayout {
  prims: Prim[];
  error: string | null;
  warning: string | null;
}

/** A fixed-size rewrite in display space. Overflow is allowed, warned about and clipped. */
export function insideNoteLayout(page: Page, anchor: Anchor, text: string, color: string): InsideNoteLayout {
  const fail = (error: string): InsideNoteLayout => ({ prims: [], error, warning: null });
  let warning: string | null = null;
  const overflow = "Replacement text does not fit inside the box at 12 pt. Text outside the box will be cut off; you can still save.";
  if (anchor.shape !== "region") return fail("Inside-box rewrites need a region, not a pin.");
  if (!text.trim()) return fail("Enter replacement text for the inside-box rewrite.");
  if (!anchor.rects.length) return fail("Inside-box rewrites need at least one rectangle.");
  for (const ch of text) {
    if (!/\s/.test(ch) && FONTS.regular.widths[ch.codePointAt(0) as number] === undefined) {
      return fail("Replacement text contains a character unsupported by the rewrite font. Use Margin or supported characters.");
    }
  }
  const [width, height] = displaySize(page);
  const rects = anchor.rects.map((r) => rectToDisplay(page, r)).sort((a, b) => a[1] - b[1] || a[0] - b[0]);
  for (let i = 0; i < rects.length; i++) {
    const r = rects[i] as Rect;
    if (!r.every(Number.isFinite) || r[0] < 0 || r[1] < 0 || r[2] > width || r[3] > height || r[0] >= r[2] || r[1] >= r[3]) {
      return fail("Inside-box rewrite rectangles must be entirely on the visible page.");
    }
    if (r[2] - r[0] <= INSIDE.padding * 2 || r[3] - r[1] < INSIDE.padding * 2 + INSIDE.line) {
      warning = "The box is too small for readable 12 pt replacement text. Text outside the box will be cut off; you can still save.";
    }
    for (let j = 0; j < i; j++) {
      const other = rects[j] as Rect;
      if (r[0] < other[2] && r[2] > other[0] && r[1] < other[3] && r[3] > other[1]) {
        return fail("Inside-box rewrite rectangles must not overlap.");
      }
    }
  }

  // Keep explicit paragraph breaks and split long words by code point, never by UTF-16 unit.
  const tokens: (string | null)[] = [];
  for (const paragraph of text.trim().replace(/\r\n?/g, "\n").split("\n")) {
    if (tokens.length) tokens.push(null);
    tokens.push(...paragraph.split(/\s+/).filter(Boolean));
  }
  const lines: Prim[] = [];
  let token = 0;
  for (const [i, r] of rects.entries()) {
    const lineWidth = Math.max(0, r[2] - r[0] - INSIDE.padding * 2);
    const capacity = (r[3] - r[1] - INSIDE.padding * 2) / INSIDE.line;
    // Continue whole lines in the next rectangle; the final rectangle may show a clipped line.
    const rows = Math.max(1, i === rects.length - 1 ? Math.ceil(capacity) : Math.floor(capacity));
    for (let row = 0; row < rows && token < tokens.length; row++) {
      let line = "";
      while (token < tokens.length) {
        const word = tokens[token] as string | null;
        if (word === null) {
          token++;
          break;
        }
        const next = line ? `${line} ${word}` : word;
        if (textWidth(next, INSIDE.size, "regular") <= lineWidth) {
          line = next;
          token++;
          continue;
        }
        if (line) break;
        let prefix = "";
        let used = 0;
        for (const ch of word) {
          const w = textWidth(ch, INSIDE.size, "regular");
          if (used + w > lineWidth) break;
          prefix += ch;
          used += w;
        }
        if (!prefix) {
          prefix = String.fromCodePoint(word.codePointAt(0) as number);
          warning ??= overflow;
        }
        line = prefix;
        const rest = word.slice(prefix.length);
        if (rest) tokens[token] = rest;
        else token++;
        break;
      }
      if (line) {
        const y = r[1] + INSIDE.padding + INSIDE.ascent + row * INSIDE.line;
        if (y + INSIDE.line - INSIDE.ascent > r[3] - INSIDE.padding) warning ??= overflow;
        lines.push({ t: "text", x: r[0] + INSIDE.padding, y, text: line, size: INSIDE.size, font: "regular", fill: readableOnWhite(color), clip: r });
      }
    }
  }
  if (token < tokens.length) warning ??= overflow;
  // Every mask precedes every text run, including for imported multi-rectangle anchors.
  const masks: Prim[] = rects.map((r) => ({ t: "rect", x: r[0], y: r[1], w: r[2] - r[0], h: r[3] - r[1], fill: STYLE.white, fillOpacity: 1 }));
  return { prims: [...masks, ...lines], error: null, warning };
}

type Row =
  | { kind: "cue"; tag: string | null; tagW: number; color: string; text: string }
  | { kind: "cuenote"; text: string }
  | { kind: "note"; text: string; color: string }
  | { kind: "standby"; text: string };

interface Block {
  kind: "cue" | "note" | "standby";
  rows: Row[];
  h: number;
  color: string;
  dashed: boolean;
  /** Invalid rewrites must not become source-covering callouts. */
  marginOnly?: boolean;
  /** Leader origin and the optional stub down from a pin. */
  ox: number;
  oy: number;
  stubY?: number;
  /** Wanted top, and the anchor's first-line top (for callouts). */
  want: number;
  firstTop: number;
  order: number;
  top: number;
  rebuild: (width: number) => Block;
}

function rowHeight(r: Row): number {
  return r.kind === "cue" || r.kind === "standby" ? STYLE.labelLine : STYLE.noteLine;
}

/** The rects of an anchor in display space (a pin as a zero-size rect). */
function displayRects(ix: DocIndex, a: Anchor): Rect[] {
  const page = ix.page(a.page);
  if (a.shape === "pin") {
    const [x, y] = toDisplay(page, a.point[0], a.point[1]);
    return [[x, y, x, y]];
  }
  return a.rects.map((r) => rectToDisplay(page, r));
}

/** Where a block wants to sit, and where its leader starts. */
type Placement = Pick<Block, "ox" | "oy" | "stubY" | "want" | "firstTop">;

function placement(ix: DocIndex, a: Anchor, side: "left" | "right"): Placement {
  const rs = displayRects(ix, a).filter((r) => r.every(Number.isFinite));
  if (!rs.length) return { ox: displaySize(ix.page(a.page))[0] / 2, oy: 4, want: 4, firstTop: 4 };
  if (a.shape === "pin") {
    const [x, y] = rs[0] as Rect;
    // Drop half a line first, so the leader runs in the gap between lines rather than through words.
    return { ox: x, oy: y + STYLE.labelLine * 0.6, stubY: y, want: y - STYLE.labelLine / 2, firstTop: y };
  }
  const top = Math.min(...rs.map((r) => r[1]));
  const first = rs.filter((r) => Math.abs(r[1] - top) < 2);
  const r = first.reduce((best, x) => (side === "right" ? (x[2] > best[2] ? x : best) : x[0] < best[0] ? x : best));
  const h = r[3] - r[1];
  return { ox: side === "right" ? r[2] : r[0], oy: r[3], want: top + (h - STYLE.labelLine) / 2, firstTop: top };
}

function cueRows(ix: DocIndex, cues: Cue[], width: number): Row[] {
  const rows: Row[] = [];
  for (const c of cues) {
    const d = ix.departments.get(c.department);
    const tag = `${d?.code ?? "?"} ${c.number}`;
    const tagW = textWidth(tag, STYLE.labelSize, "bold") + 5;
    wrap(c.label, STYLE.labelSize, "regular", Math.max(10, width - tagW - 3)).forEach((text, i) =>
      rows.push({ kind: "cue", tag: i === 0 ? tag : null, tagW, color: d?.color ?? STYLE.grey, text }),
    );
    if (c.notes) for (const text of wrap(c.notes, STYLE.noteSize, "italic", width - 6)) rows.push({ kind: "cuenote", text });
  }
  return rows;
}

function block(kind: Block["kind"], rows: Row[], color: string, dashed: boolean, pos: Placement, order: number, rebuild: (w: number) => Block): Block {
  const h = rows.reduce((s, r) => s + rowHeight(r), 0) + 1.5;
  return { kind, rows, h, color, dashed, ...pos, order, top: pos.want, rebuild };
}

/** Pack blocks top to bottom in [top, bottom], keeping order; returns the ones that didn't fit. */
function pack(blocks: Block[], top: number, bottom: number): Block[] {
  blocks.sort((a, b) => a.want - b.want || a.order - b.order);
  const run = () => {
    let prevBottom = top - STYLE.blockGap;
    for (const b of blocks) {
      b.top = Math.max(b.want, prevBottom + STYLE.blockGap);
      prevBottom = b.top + b.h;
    }
    let limit = bottom;
    for (let i = blocks.length - 1; i >= 0; i--) {
      const b = blocks[i] as Block;
      if (b.top + b.h > limit) b.top = limit - b.h;
      limit = b.top - STYLE.blockGap;
    }
  };
  run();
  const overflow: Block[] = [];
  // Pushed above the column top: drop the latest blocks (lowest priority) until the rest fit.
  while (blocks.length && (blocks[0] as Block).top < top - 0.01) {
    overflow.unshift(blocks.pop() as Block);
    run();
  }
  return overflow;
}

/** Each standby's text for these departments (null: not drawn). Computed once for the whole show. */
export function standbyLabels(ix: DocIndex, departments: ReadonlySet<string> | null): Map<string, string | null> {
  const out = new Map<string, string | null>();
  for (const s of standbyCoverage(ix)) out.set(s.standby.id, standbyText(ix, s.covered, departments ?? undefined));
  return out;
}

/** The outer margin: right on odd printed pages (rectos), left on even; PDF page parity when unlabelled. */
export function outerSide(ix: DocIndex, pageNo: number): "left" | "right" {
  const printed = Number.parseInt(ix.page(pageNo).label, 10);
  return (Number.isNaN(printed) ? pageNo : printed) % 2 === 0 ? "left" : "right";
}

function hatch(r: Rect, step: number): [number, number, number, number][] {
  const [x0, y0, x1, y1] = r;
  const out: [number, number, number, number][] = [];
  for (let c = x0 - y1; c <= x1 - y0; c += step) {
    const pts: [number, number][] = [];
    const yAtX0 = x0 - c;
    const yAtX1 = x1 - c;
    const xAtY0 = y0 + c;
    const xAtY1 = y1 + c;
    if (yAtX0 >= y0 && yAtX0 <= y1) pts.push([x0, yAtX0]);
    if (yAtX1 >= y0 && yAtX1 <= y1) pts.push([x1, yAtX1]);
    if (xAtY0 > x0 && xAtY0 < x1) pts.push([xAtY0, y0]);
    if (xAtY1 > x0 && xAtY1 < x1) pts.push([xAtY1, y1]);
    if (pts.length >= 2) out.push([(pts[0] as [number, number])[0], (pts[0] as [number, number])[1], (pts[1] as [number, number])[0], (pts[1] as [number, number])[1]]);
  }
  return out;
}

export function layoutPage(ix: DocIndex, pageNo: number, opts: LayoutOptions, standbys: Map<string, string | null> = standbyLabels(ix, opts.departments)): PageLayout {
  const page = ix.page(pageNo);
  const [W, H] = displaySize(page);
  const side = outerSide(ix, pageNo);
  const margin = opts.margin ?? STYLE.margin;
  const colX0 = side === "right" ? W - STYLE.edge - margin : STYLE.edge;
  const colX1 = colX0 + margin;
  const colW = margin;
  const stampH = opts.stamp ? STYLE.stampSize + 4 : 0;
  const colTop = 4;
  const colBottom = H - 4 - stampH;
  const prims: Prim[] = [];
  const keepDept = (id: string) => !opts.departments || opts.departments.has(id);
  const keepLayer = (id: string) => !opts.layers || opts.layers.has(id);
  const order = new Map<string, number>();
  const anchored: { id: string; anchor: Anchor }[] = [...ix.doc.moments, ...ix.doc.standbys, ...ix.doc.notes].filter((x) => x.anchor.page === pageNo);
  anchored.sort(ix.compareAnchored).forEach((x, i) => order.set(x.id, i));

  // What's visible on this page.
  const moments: { m: Moment; cues: Cue[] }[] = [];
  for (const m of ix.doc.moments) {
    if (m.anchor.page !== pageNo) continue;
    const cues = ix.cuesOf(m.id).filter((c) => keepDept(c.department));
    if (cues.length) moments.push({ m, cues });
  }
  const notes: Note[] = ix.doc.notes.filter((n) => n.anchor.page === pageNo && keepLayer(n.layer) && ix.layers.has(n.layer));
  const sbs: { s: Standby; text: string }[] = [];
  for (const s of ix.doc.standbys) {
    const text = standbys.get(s.id);
    if (s.anchor.page === pageNo && text) sbs.push({ s, text });
  }

  // Marks over the script: notes first (under), then cues, then standbys.
  const inside = new Map<string, InsideNoteLayout>();
  for (const n of notes) {
    const color = ix.layers.get(n.layer)?.color ?? STYLE.grey;
    if (n.placement === "inside") {
      const result = n.style === "box"
        ? insideNoteLayout(page, n.anchor, n.text, color)
        : { prims: [], error: "Inside-box rewrites require the box style.", warning: null };
      inside.set(n.id, result);
      prims.push(...result.prims);
      continue;
    }
    for (const r of displayRects(ix, n.anchor)) {
      if (n.anchor.shape === "pin") {
        prims.push({ t: "poly", points: [[r[0] - 2.5, r[1] - 5], [r[0] + 2.5, r[1] - 5], [r[0], r[1]]], fill: color });
      } else if (n.style === "strike") {
        prims.push({ t: "rect", x: r[0], y: r[1], w: r[2] - r[0], h: r[3] - r[1], fill: color, fillOpacity: 0.06, stroke: color, strokeWidth: 0.6 });
        for (const [x1, y1, x2, y2] of hatch(r, 4)) prims.push({ t: "line", x1, y1, x2, y2, stroke: color, width: 0.4, opacity: 0.55 });
      } else {
        prims.push({ t: "rect", x: r[0], y: r[1], w: r[2] - r[0], h: r[3] - r[1], stroke: color, strokeWidth: 0.6, dash: [2, 1.5] });
      }
    }
  }
  for (const { m, cues } of moments) {
    const color = ix.departments.get((cues[0] as Cue).department)?.color ?? STYLE.grey;
    const rs = displayRects(ix, m.anchor);
    if (m.anchor.shape === "pin") {
      const [x, y] = rs[0] as Rect;
      prims.push({ t: "circle", cx: x, cy: y, r: 1.3, fill: color, stroke: STYLE.white, strokeWidth: 0.3 });
      prims.push({ t: "line", x1: x, y1: y, x2: x, y2: y - 8, stroke: color, width: 0.6 });
      prims.push({ t: "poly", points: [[x, y - 8], [x + 6, y - 10], [x, y - 12]], fill: color });
    } else {
      for (const r of rs) prims.push({ t: "rect", x: r[0], y: r[1], w: r[2] - r[0], h: r[3] - r[1], fill: color, fillOpacity: 0.16, stroke: color, strokeWidth: 0.6, strokeOpacity: 0.9 });
      if (cues.length > 1) {
        const top = Math.min(...rs.map((r) => r[1]));
        const first = rs.filter((r) => Math.abs(r[1] - top) < 2).reduce((a, b) => (b[0] < a[0] ? b : a));
        cues.forEach((c, i) =>
          prims.push({ t: "rect", x: first[0] - 2.5, y: first[1] + 0.3 + i * 2.6, w: 2.2, h: 2.2, fill: ix.departments.get(c.department)?.color ?? STYLE.grey }),
        );
      }
    }
  }
  for (const { s } of sbs) {
    const rs = displayRects(ix, s.anchor);
    if (s.anchor.shape === "pin") {
      const [x, y] = rs[0] as Rect;
      prims.push({ t: "poly", points: [[x, y - 3], [x + 3, y], [x, y + 3], [x - 3, y]], fill: STYLE.ink });
    } else {
      for (const r of rs) prims.push({ t: "rect", x: r[0], y: r[1], w: r[2] - r[0], h: r[3] - r[1], stroke: STYLE.ink, strokeWidth: 0.6, dash: [1, 1] });
    }
  }

  // Label blocks.
  const blocks: Block[] = [];
  for (const { m, cues } of moments) {
    const color = cues.length > 1 ? STYLE.ink : (ix.departments.get((cues[0] as Cue).department)?.color ?? STYLE.grey);
    const make = (w: number): Block => block("cue", cueRows(ix, cues, w), color, false, placement(ix, m.anchor, side), order.get(m.id) ?? 0, make);
    blocks.push(make(colW));
  }
  for (const { s, text } of sbs) {
    const make = (w: number): Block =>
      block("standby", wrap(text, STYLE.labelSize, "bold", w - 4).map((t) => ({ kind: "standby", text: t })), STYLE.ink, false, placement(ix, s.anchor, side), order.get(s.id) ?? 0, make);
    blocks.push(make(colW));
  }
  for (const n of notes) {
    const rewrite = inside.get(n.id);
    if (rewrite && !rewrite.error) continue;
    const layer = ix.layers.get(n.layer);
    const color = layer?.color ?? STYLE.grey;
    const text = rewrite?.error ? `WARNING: ${rewrite.error}\n${n.text || "(empty replacement text)"}` : n.text || layer?.name || "";
    const make = (w: number): Block => {
      const b = block("note", wrap(text, STYLE.noteSize, "italic", w - 6).map((t) => ({ kind: "note", text: t, color })), color, n.style === "box", placement(ix, n.anchor, side), order.get(n.id) ?? 0, make);
      if (rewrite?.error) b.marginOnly = true;
      return b;
    };
    blocks.push(make(colW));
  }

  let callouts = pack(blocks, colTop, colBottom);
  callouts.push(...blocks.filter((b) => Math.abs(b.top - b.want) > STYLE.calloutDrift));
  const kept = blocks.filter((b) => !callouts.includes(b));
  callouts.push(...pack(kept, colTop, colBottom));

  for (const b of kept) {
    leader(prims, b, side, colX0, colX1);
    drawBlock(prims, b, colX0, b.top, colW, false);
  }

  // Callouts: beside the mark, just above its first line, on white, with a border.
  const placed: { x: number; top: number; w: number; h: number }[] = [];
  callouts.sort((a, b) => b.firstTop - a.firstTop);
  for (const c of callouts) {
    if (c.marginOnly) {
      // Even when the usual near-line packing fails, leave the selected source region untouched.
      const b = c.rebuild(colW);
      drawBlock(prims, b, colX0, Math.max(colTop, Math.min(c.want, colBottom - b.h)), colW, false);
      continue;
    }
    const cw = STYLE.calloutWidth;
    const b = c.rebuild(cw);
    const x = Math.min(Math.max(4, c.ox - cw / 2), W - cw - 4);
    let top = c.firstTop - b.h - 1;
    for (let moved = true, n = 0; moved && n < 50; n++) {
      moved = false;
      for (const p of placed) {
        if (x < p.x + p.w && x + cw > p.x && top < p.top + p.h && top + b.h > p.top) {
          top = p.top - b.h - 1.5;
          moved = true;
        }
      }
    }
    top = Math.max(2, top);
    placed.push({ x, top, w: cw, h: b.h });
    prims.push({ t: "line", x1: c.ox, y1: c.oy, x2: x + cw / 2, y2: top + b.h, stroke: c.color, width: 0.5, opacity: 0.9 });
    drawBlock(prims, b, x, top, cw, true);
  }

  if (opts.stamp) {
    const w = textWidth(opts.stamp, STYLE.stampSize, "regular");
    const x = side === "right" ? colX1 - w : colX0;
    prims.push({ t: "text", x, y: H - 6, text: opts.stamp, size: STYLE.stampSize, font: "regular", fill: STYLE.grey });
  }
  return { page: pageNo, width: W, height: H, side, prims, callouts: callouts.length };
}

function leader(prims: Prim[], b: Block, side: "left" | "right", colX0: number, colX1: number): void {
  const labelY = b.top + Math.min(STYLE.labelLine, b.h) / 2;
  const base = { stroke: b.color, width: 0.5, opacity: 0.85, ...(b.kind === "note" ? { dash: [1.5, 1.2] } : {}) };
  if (b.stubY !== undefined) prims.push({ t: "line", x1: b.ox, y1: b.stubY, x2: b.ox, y2: b.oy, ...base });
  if (side === "right") {
    const elbow = colX0 - 4;
    if (b.ox < elbow) prims.push({ t: "line", x1: b.ox, y1: b.oy, x2: elbow, y2: b.oy, ...base });
    prims.push({ t: "line", x1: Math.max(elbow, b.ox), y1: b.oy, x2: colX0 - 0.5, y2: labelY, ...base });
  } else {
    const elbow = colX1 + 4;
    if (b.ox > elbow) prims.push({ t: "line", x1: b.ox, y1: b.oy, x2: elbow, y2: b.oy, ...base });
    prims.push({ t: "line", x1: Math.min(elbow, b.ox), y1: b.oy, x2: colX1 + 1.5, y2: labelY, ...base });
  }
}

function drawBlock(prims: Prim[], b: Block, x: number, top: number, w: number, border: boolean): void {
  prims.push({ t: "rect", x: x - 1, y: top, w: w + 2, h: b.h, fill: STYLE.white, fillOpacity: 0.88, ...(border ? { stroke: b.color, strokeWidth: 0.5 } : {}) });
  if (b.kind === "note") prims.push({ t: "line", x1: x + 0.8, y1: top + 0.5, x2: x + 0.8, y2: top + b.h - 0.5, stroke: b.color, width: 1.2, ...(b.dashed ? { dash: [1.5, 1] } : {}) });
  if (b.kind === "standby") prims.push({ t: "line", x1: x + 0.6, y1: top + 0.5, x2: x + 0.6, y2: top + b.h - 0.5, stroke: STYLE.ink, width: 1.2 });
  let y = top;
  for (const r of b.rows) {
    const lh = rowHeight(r);
    if (r.kind === "cue") {
      if (r.tag) {
        prims.push({ t: "rect", x, y: y + 0.4, w: r.tagW, h: lh - 0.6, fill: r.color });
        prims.push({ t: "text", x: x + 2.5, y: y + lh * 0.78, text: r.tag, size: STYLE.labelSize, font: "bold", fill: inkOn(r.color) });
      }
      prims.push({ t: "text", x: x + r.tagW + 3, y: y + lh * 0.78, text: r.text, size: STYLE.labelSize, font: "regular", fill: STYLE.ink });
    } else if (r.kind === "standby") {
      prims.push({ t: "text", x: x + 4, y: y + lh * 0.78, text: r.text, size: STYLE.labelSize, font: "bold", fill: STYLE.ink });
    } else {
      prims.push({ t: "text", x: x + 5, y: y + lh * 0.8, text: r.text, size: STYLE.noteSize, font: "italic", fill: r.kind === "note" ? readableOnWhite(r.color) : STYLE.grey });
    }
    y += lh;
  }
}
